import React, { useState, useEffect } from 'react';
import { attendanceAPI } from '../../../services/AdminPortal/staffworkAPI';
import { Clock } from 'lucide-react';

const TimeSheetDisplay = ({ staffId }) => {
  const [apiData, setApiData] = useState(null);
  const [timeSheetData, setTimeSheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const startTime = 9; // 9 AM
  const endTime = 21;  // 9 PM
  const totalHoursInTimeline = endTime - startTime;

  useEffect(() => {
    const fetchTimeSheet = async () => {
      if (!staffId) {
        setLoading(false);
        setError("User ID is required.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await attendanceAPI.getTodayTimeSheet(staffId);
        setApiData(data);
        transformDataForDisplay(data);
      } catch (err) {
        console.error("Error fetching timesheet:", err);
        // Check if the error is specifically "No login recorded for today."
        if (err && err.message === 'No login recorded for today.') {
          setApiData({ // Set default structure for "no login"
            loginTime: null,
            breaks: [],
            summary: { totalWorkingTime: '0H 0M', regularTime: '0H 0M', totalBreakTime: '0H 0M', overtime: '0H 0M', defaultRegularHoursPerDay: '8h 0m' }
          });
          setTimeSheetData({ timeline: [] });
          setError(null); // Clear error state as we are handling this case
        } else {
          setError(err.message || 'Failed to load time sheet data.');
          setTimeSheetData(null);
          setApiData(null);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTimeSheet();
    // eslint-disable-next-line
  }, [staffId]);

  const transformDataForDisplay = (rawData) => {
    if (!rawData || !rawData.loginTime) {
      setTimeSheetData({ timeline: [] });
      return;
    }
    const timeline = [];
    const now = new Date();
    let lastEventTime = new Date(rawData.loginTime);

    (rawData.breaks || []).forEach(breakItem => {
      if (new Date(breakItem.breakInTime) > lastEventTime) {
        timeline.push({
          start: lastEventTime.toISOString(),
          end: breakItem.breakInTime,
          type: 'working',
        });
      }
      lastEventTime = new Date(breakItem.breakInTime);
      const breakEndTime = breakItem.breakOutTime ? new Date(breakItem.breakOutTime) : now;
      timeline.push({
        start: breakItem.breakInTime,
        end: breakEndTime.toISOString(),
        type: 'break',
      });
      lastEventTime = breakEndTime;
    });

    const effectiveLogoutTime = rawData.logoutTime ? new Date(rawData.logoutTime) : now;
    if (effectiveLogoutTime > lastEventTime) {
      timeline.push({
        start: lastEventTime.toISOString(),
        end: effectiveLogoutTime.toISOString(),
        type: 'working',
      });
    }
    setTimeSheetData({ timeline });
  };

  if (loading) {
    return <div className="animate-pulse text-gray-600">Loading Time Sheet...</div>;
  }

  if (error) { // This will now only show for unhandled errors
    return <div className="text-red-500 p-4">Error: {error}</div>;
  }

  // If apiData is null (e.g. initial state or unhandled error not setting default)
  // OR if apiData.loginTime is null specifically AND we didn't set a default summary (which we do now)
  if (!apiData) {
    return <div className="text-gray-600 p-4">No login recorded for today.</div>;
  }

  const renderTimeline = () => {
    if (!timeSheetData || !timeSheetData.timeline || timeSheetData.timeline.length === 0) {
      return <div className="text-center text-gray-500 my-4">No activity to display on timeline.</div>;
    }

    const timelineSegments = timeSheetData.timeline.map((segment, index) => {
      const startHour = new Date(segment.start).getHours();
      const startMinute = new Date(segment.start).getMinutes();
      const endHour = segment.end ? new Date(segment.end).getHours() : endTime;
      const endMinute = segment.end ? new Date(segment.end).getMinutes() : 0;

      const segmentStartInMinutes = (startHour - startTime) * 60 + startMinute;
      let segmentEndInMinutes = (endHour - startTime) * 60 + endMinute;

      if (segmentEndInMinutes <= segmentStartInMinutes && new Date(segment.end) > new Date(segment.start)) {
        segmentEndInMinutes = segmentStartInMinutes + 1;
      }

      const totalMinutesInDayView = totalHoursInTimeline * 60;
      const leftPercentage = (segmentStartInMinutes / totalMinutesInDayView) * 100;
      let widthPercentage = ((segmentEndInMinutes - segmentStartInMinutes) / totalMinutesInDayView) * 100;
      if (widthPercentage < 0) widthPercentage = 0;

      let backgroundColorClass = '';
      if (segment.type === 'working') {
        backgroundColorClass = 'bg-green-500';
      } else if (segment.type === 'break') {
        backgroundColorClass = 'bg-red-500';
      }

      return (
        <div
          key={index}
          className={`absolute top-0 h-full rounded-md ${backgroundColorClass}`}
          style={{ left: `${Math.max(0, leftPercentage)}%`, width: `${Math.max(0.1, widthPercentage)}%` }}
        />
      );
    });

    const timeTicks = Array.from({ length: totalHoursInTimeline + 1 }, (_, i) => {
      const hour = startTime + i;
      return (
        <div
          key={hour}
          className="absolute top-8 text-xs text-gray-500 -translate-x-1/2"
          style={{ left: `${((hour - startTime) / totalHoursInTimeline) * 100}%` }}
        >
          {hour > 12 ? `${hour - 12} PM` : `${hour === 0 ? 12 : (hour === 12 ? 12 : hour)} ${hour < 12 || hour === 24 ? 'AM' : 'PM'}`}
        </div>
      );
    });

    return (
      <div className="relative bg-gray-200 rounded-md h-6 mb-5">
        <div className="absolute top-0 left-0 w-full h-full rounded-md overflow-hidden">
          {timelineSegments}
        </div>
        <div className="relative flex justify-between w-full h-full pointer-events-none">
          {timeTicks}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#131313] p-6 rounded-lg shadow-md border-2 border-[#fff]/10">
      <h2 className="text-xl font-semibold mb-4">Today's Time Sheet</h2>
      <div className="mb-3 flex items-center space-x-3">
        <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
        <span>Working Hours</span>
        <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
        <span>Break</span>
      </div>
      {renderTimeline()}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg border-2 border-[#fff]/10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-gray-400 text-sm">
                          TOTAL WORKING TIME
                        </div>
                        <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z" fill="#FFCA20"/>
</svg>

                      </div>
                      <div className="text-xl font-bold">
                        {apiData?.summary?.totalWorkingTime || '0H 0M'}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border-2 border-[#fff]/10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-gray-400 text-sm">REGULAR HOURS</div>
                        <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z" fill="#FFCA20"/>
</svg>

                      </div>
                      <div className="text-xl font-bold">
                        {apiData?.summary?.regularTime || '0H 0M'}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border-2 border-[#fff]/10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-gray-400 text-sm">BREAK HOURS</div>
                       <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z" fill="#FFCA20"/>
</svg>

                      </div>
                      <div className="text-xl font-bold">
                        {apiData?.summary?.totalBreakTime || '0H 0M'}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border-2 border-[#fff]/10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-gray-400 text-sm">OVERTIME</div>
                        <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.75 0.75C17.9375 0.75 23 5.8125 23 12C23 18.2344 17.9375 23.25 11.75 23.25C5.51562 23.25 0.5 18.2344 0.5 12C0.5 5.8125 5.51562 0.75 11.75 0.75ZM11.75 21C16.6719 21 20.75 16.9688 20.75 12C20.75 7.07812 16.6719 3 11.75 3C6.78125 3 2.75 7.07812 2.75 12C2.75 16.9688 6.78125 21 11.75 21ZM12.875 12.1875L15.4062 14.1094C15.9219 14.4844 16.0156 15.1875 15.6406 15.7031C15.4062 15.9844 15.0781 16.125 14.7031 16.125C14.4688 16.125 14.2344 16.0781 14.0469 15.9375L11.0469 13.6875C10.7656 13.4531 10.625 13.125 10.625 12.75V7.125C10.625 6.51562 11.0938 6 11.75 6C12.3594 6 12.875 6.51562 12.875 7.125V12.1875Z" fill="#FFCA20"/>
</svg>

                      </div>
                      <div className="text-xl font-bold">
                        {apiData?.summary?.overtime || '0H 0M'}
                      </div>
                    </div>
                  </div>

    </div>
  );
};

export default TimeSheetDisplay;